import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, toast } from '../App';
import { useAuth } from '../contexts/AuthContext';
import { 
  Target, Plus, Clock, AlertTriangle, User, Trash2, ChevronUp, ChevronDown, 
  Link, Loader2, ExternalLink, Globe, Copy, Check, Users, ArrowLeft, RefreshCw,
  Filter, Search, Wand2, Sparkles, Archive, RotateCcw, Pencil, Calendar
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import HourlyAlertPopup from '../components/HourlyAlertPopup';

const PRIORITY_CONFIG = {
  urgent: { label: 'URGENT', color: 'red', icon: '🔴', bg: 'bg-red-500/10', border: 'border-red-500/30' },
  normal: { label: 'NORMAL', color: 'yellow', icon: '🟡', bg: 'bg-yellow-500/10', border: 'border-yellow-500/30' },
  low: { label: 'BASSE', color: 'green', icon: '🟢', bg: 'bg-green-500/10', border: 'border-green-500/30' },
};

const isUrl = (text) => text && (text.startsWith('http://') || text.startsWith('https://'));

// Composant Badge IA avec baguette magique
const AIBadge = ({ small = false }) => (
  <span 
    className={`inline-flex items-center gap-0.5 ${small ? 'text-[8px] px-1 py-0.5' : 'text-[9px] px-1.5 py-0.5'} font-semibold text-violet-300 bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 border border-violet-400/40 rounded shadow-sm`}
    title="Suggestion IA"
  >
    <Wand2 className={`${small ? 'w-2 h-2' : 'w-2.5 h-2.5'}`} />
    <span>IA</span>
  </span>
);

const CiblesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [targets, setTargets] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [showPriorityDialog, setShowPriorityDialog] = useState(false);
  const [showClearAllDialog, setShowClearAllDialog] = useState(false);
  const [showTakeDialog, setShowTakeDialog] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  
  // Form states
  const [newTitle, setNewTitle] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newPriority, setNewPriority] = useState('normal');
  const [newVa, setNewVa] = useState(false);
  const [selectedSites, setSelectedSites] = useState([]);
  const [takeSites, setTakeSites] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Suggestions IA
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // Archivées
  const [archivedTargets, setArchivedTargets] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);

  // Édition
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editUrl, setEditUrl] = useState('');
  const [editPriority, setEditPriority] = useState('normal');
  const [editVa, setEditVa] = useState(false);
  const [editSites, setEditSites] = useState([]);

  const isAmiral = user?.grade === 'amiral';
  const publicUrl = `${window.location.origin}/s/secret-target-info`;

  const loadTargets = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const [targetsRes, sitesRes] = await Promise.all([
        axios.get(`${API}/targets`),
        axios.get(`${API}/wordpress-sites`).catch(() => ({ data: [] })),
      ]);
      setTargets(targetsRes.data.targets || []);
      setSites(sitesRes.data || []);
    } catch (error) {
      console.error('Error loading targets:', error);
      toast.error('Erreur lors du chargement des cibles');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadTargets();
    const interval = setInterval(() => loadTargets(), 30000);
    return () => clearInterval(interval);
  }, [loadTargets]);

  const loadArchived = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/targets/archived`);
      setArchivedTargets(res.data.targets || []);
      setArchivedCount(res.data.count || 0);
    } catch (error) {
      console.error('Error loading archived:', error);
    }
  }, []);

  useEffect(() => {
    loadArchived();
  }, [loadArchived]);

  const handleRestore = async (targetId) => {
    try {
      await axios.post(`${API}/targets/${targetId}/restore`);
      toast.success('Cible restaurée');
      loadTargets();
      loadArchived();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la restauration');
    }
  };

  const openEditDialog = (target) => {
    setEditTarget(target);
    setEditTitle(target.title || '');
    setEditUrl(target.url || '');
    setEditPriority(target.priority || 'normal');
    setEditVa(target.va || false);
    setEditSites(target.sites || []);
    setShowEditDialog(true);
  };

  const handleEdit = async () => {
    if (!editTarget) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/targets/${editTarget.id}`, {
        title: editTitle.trim(),
        url: editUrl.trim() || null,
        priority: editPriority,
        va: editVa,
        sites: editSites.length > 0 ? editSites : null,
      });
      toast.success('Cible modifiée');
      setShowEditDialog(false);
      setEditTarget(null);
      loadTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setSubmitting(false);
    }
  };

  const copyPublicUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success('Lien public copié !');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Erreur lors de la copie');
    }
  };

  // Fonction pour récupérer les suggestions IA
  const fetchAISuggestions = useCallback(async (title, url = null) => {
    if (!title || title.length < 10) {
      setAiSuggestions([]);
      return;
    }
    
    setLoadingSuggestions(true);
    try {
      const res = await axios.post(`${API}/targets/suggest-sites`, { 
        title, 
        url: url || null 
      });
      if (res.data.success && res.data.suggestions) {
        setAiSuggestions(res.data.suggestions);
      }
    } catch (error) {
      console.error('Erreur suggestions:', error);
      setAiSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  }, []);

  // Debounce pour les suggestions IA
  useEffect(() => {
    const timer = setTimeout(() => {
      if (newTitle.length >= 10 && showAddDialog) {
        fetchAISuggestions(newTitle, newUrl);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [newTitle, newUrl, showAddDialog, fetchAISuggestions]);

  const handleInputChange = async (value) => {
    setNewTitle(value);
    if (isUrl(value) && value.length > 15) {
      setScraping(true);
      try {
        const res = await axios.post(`${API}/targets/scrape-url`, { url: value });
        if (res.data.success && res.data.title) {
          setNewTitle(res.data.title);
          setNewUrl(res.data.url);
          toast.success('Titre récupéré automatiquement');
          // Récupérer les suggestions avec le nouveau titre
          fetchAISuggestions(res.data.title, res.data.url);
        }
      } catch (error) {
        setNewUrl(value);
      } finally {
        setScraping(false);
      }
    } else if (!isUrl(value)) {
      setNewUrl('');
    }
  };

  // Appliquer une suggestion IA
  const applySuggestion = (siteId) => {
    if (selectedSites.includes(siteId)) {
      setSelectedSites(selectedSites.filter(id => id !== siteId));
    } else {
      setSelectedSites([...selectedSites, siteId]);
    }
  };

  const handleAddTarget = async () => {
    if (!newTitle.trim()) {
      toast.error('Veuillez saisir un sujet');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/targets`, {
        title: newTitle.trim(),
        url: newUrl || null,
        sites: selectedSites.length > 0 ? selectedSites : null,
        priority: newPriority,
        va: newVa,
      });
      
      // Enregistrer le choix pour apprentissage si des sites ont été sélectionnés
      if (selectedSites.length > 0 && res.data.target?.id) {
        try {
          await axios.post(`${API}/targets/record-choice`, {
            target_id: res.data.target.id,
            site_ids: selectedSites
          });
        } catch (e) {
          console.warn('Apprentissage non enregistré:', e);
        }
      }
      
      toast.success('Cible ajoutée');
      setNewTitle('');
      setNewUrl('');
      setSelectedSites([]);
      setAiSuggestions([]);
      setNewPriority('normal');
      setNewVa(false);
      setShowAddDialog(false);
      loadTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setSubmitting(false);
    }
  };

  // Ouvrir le dialog de prise avec suggestions IA
  const openTakeDialog = async (target) => {
    setSelectedTarget(target);
    setTakeSites(target.sites || []);
    setShowTakeDialog(true);
    
    // Charger les suggestions IA pour cette cible
    if (target.title && target.title.length > 5) {
      setLoadingSuggestions(true);
      try {
        const res = await axios.post(`${API}/targets/suggest-sites`, {
          title: target.title,
          url: target.url || null
        });
        if (res.data.success && res.data.suggestions) {
          setAiSuggestions(res.data.suggestions);
        }
      } catch (error) {
        console.error('Erreur suggestions:', error);
      } finally {
        setLoadingSuggestions(false);
      }
    }
  };

  const handleTake = async () => {
    if (!selectedTarget) return;
    setSubmitting(true);
    try {
      // Envoyer les sites sélectionnés pour l'apprentissage
      await axios.post(`${API}/targets/${selectedTarget.id}/take`, {
        site_ids: takeSites.length > 0 ? takeSites : null
      });
      toast.success('Cible réservée');
      setShowTakeDialog(false);
      setSelectedTarget(null);
      setTakeSites([]);
      setAiSuggestions([]);
      loadTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  // Prise rapide sans dialog (pour compatibilité)
  const handleQuickTake = async (targetId) => {
    try {
      await axios.post(`${API}/targets/${targetId}/take`);
      toast.success('Cible réservée');
      loadTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRelease = async () => {
    if (!selectedTarget) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/targets/${selectedTarget.id}/release`);
      toast.success('Cible libérée');
      setShowReleaseDialog(false);
      setSelectedTarget(null);
      loadTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (targetId) => {
    try {
      await axios.delete(`${API}/targets/${targetId}`);
      toast.success('Cible supprimée');
      loadTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUpdatePriority = async () => {
    if (!selectedTarget) return;
    setSubmitting(true);
    try {
      await axios.put(`${API}/targets/${selectedTarget.id}/priority`, { priority: newPriority });
      toast.success('Priorité mise à jour');
      setShowPriorityDialog(false);
      setSelectedTarget(null);
      loadTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearAll = async () => {
    setSubmitting(true);
    try {
      const res = await axios.delete(`${API}/targets/clear-all`);
      toast.success(res.data.message || 'Liste vidée');
      setShowClearAllDialog(false);
      loadTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' });
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    if (!site) return siteId.slice(0, 6);
    try {
      const hostname = new URL(site.url).hostname.replace('www.', '');
      return hostname.split('.')[0].toUpperCase();
    } catch {
      return site.name?.slice(0, 8) || siteId.slice(0, 6);
    }
  };

  // Filtrage des cibles
  const filteredTargets = targets.filter(target => {
    const matchesSearch = !searchTerm || 
      target.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = filterPriority === 'all' || target.priority === filterPriority;
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'available' && !target.taken_by) ||
      (filterStatus === 'taken' && target.taken_by);
    return matchesSearch && matchesPriority && matchesStatus;
  });

  const availableTargets = filteredTargets
    .filter(t => !t.taken_by)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Plus récent en haut
  const takenTargets = filteredTargets.filter(t => t.taken_by)
    .sort((a, b) => new Date(b.taken_at) - new Date(a.taken_at));

  // Stats
  const stats = {
    total: targets.length,
    available: targets.filter(t => !t.taken_by).length,
    taken: targets.filter(t => t.taken_by).length,
    urgent: targets.filter(t => t.priority === 'urgent' && !t.taken_by).length,
    va: targets.filter(t => t.va && !t.taken_by).length,
    late: targets.filter(t => t.is_late && !t.taken_by).length,
  };

  // Répartition par utilisateur
  const userDistribution = takenTargets.reduce((acc, t) => {
    if (t.taken_by?.name) {
      const name = t.taken_by.name;
      acc[name] = (acc[name] || 0) + 1;
    }
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Target className="w-12 h-12 mx-auto mb-4 text-amber-400 animate-pulse" />
          <p className="text-slate-400">Chargement des cibles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-3 md:p-4" data-testid="cibles-page">
      <div className="max-w-7xl mx-auto">
        {/* Header compact - Tactical Navy */}
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-slate-400 hover:text-sky-400 h-7 px-2"
              data-testid="btn-back-home"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
            </Button>
            <h1 className="text-xl md:text-2xl font-orbitron uppercase tracking-wider text-sky-400 flex items-center gap-2">
              <Target className="w-5 h-5" />
              Cibles
            </h1>
          </div>
          <div className="flex gap-1.5">
            <Button 
              onClick={() => loadTargets(true)} 
              disabled={refreshing}
              size="sm"
              variant="outline"
              className="h-7 text-xs border-sky-900/60 text-slate-300 hover:bg-sky-950/30 hover:border-sky-500"
              data-testid="btn-refresh"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              onClick={copyPublicUrl}
              size="sm"
              variant="outline"
              className="h-7 text-xs border-emerald-700/50 text-emerald-400 hover:bg-emerald-500/10"
              data-testid="btn-copy-public-link"
            >
              {copied ? <Check className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
            </Button>
            <Button
              onClick={() => setShowAddDialog(true)}
              size="sm"
              className="h-7 text-xs bg-sky-600 hover:bg-sky-500 text-white font-semibold shadow-md shadow-sky-600/20"
              data-testid="btn-add-target"
            >
              <Plus className="w-3 h-3 mr-1" />
              Ajouter
            </Button>
          </div>
        </div>

        {/* KPI Cards - Tactical Navy Style */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-900/80 border border-slate-800 rounded text-slate-200">
            <span className="text-[10px] uppercase tracking-wider text-slate-400">Total</span>
            <span className="text-lg font-bold">{stats.total}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-sky-950/30 border border-sky-500/40 rounded text-sky-200">
            <span className="text-[10px] uppercase tracking-wider text-sky-400">Dispo</span>
            <span className="text-lg font-bold">{stats.available}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-950/30 border border-blue-500/40 rounded text-blue-200">
            <span className="text-[10px] uppercase tracking-wider text-blue-400">Réservées</span>
            <span className="text-lg font-bold">{stats.taken}</span>
          </div>
          {stats.late > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/50 border border-slate-600/50 rounded text-slate-300">
              <span className="text-[10px] uppercase tracking-wider text-slate-400">+24h</span>
              <span className="text-lg font-bold">{stats.late}</span>
            </div>
          )}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-rose-950/30 border border-rose-500/40 rounded text-rose-200">
            <span className="text-[10px] uppercase tracking-wider text-rose-400">Urgentes</span>
            <span className="text-lg font-bold">{stats.urgent}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-950/30 border border-amber-500/40 rounded text-amber-200">
            <span className="text-[10px] uppercase tracking-wider text-amber-400">VA</span>
            <span className="text-lg font-bold">{stats.va}</span>
          </div>
          {archivedCount > 0 && (
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded transition-colors cursor-pointer ${
                showArchived 
                  ? 'bg-slate-700/60 border border-slate-500/60 text-slate-100' 
                  : 'bg-slate-900/50 border border-slate-700/40 text-slate-400 hover:text-slate-200'
              }`}
              data-testid="toggle-archived"
            >
              <Archive className="w-3 h-3" />
              <span className="text-[10px] uppercase tracking-wider">Archives</span>
              <span className="text-lg font-bold">{archivedCount}</span>
            </button>
          )}
        </div>

        {/* Filters - Tactical Navy Style */}
        <div className="flex flex-wrap items-center gap-2 mb-4 p-2 bg-slate-900/70 border border-sky-950/80 rounded">
          <div className="flex-1 min-w-[180px]">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-8 text-sm bg-slate-900/80 border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-sky-400 focus:ring-1 focus:ring-sky-400"
                data-testid="search-input"
              />
            </div>
          </div>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-slate-200 focus:border-sky-400"
            data-testid="filter-priority"
          >
            <option value="all">Toutes priorités</option>
            <option value="urgent">🔴 Urgent</option>
            <option value="normal">🟡 Normal</option>
            <option value="low">🟢 Basse</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="bg-slate-900 border border-slate-700 rounded px-2.5 py-1.5 text-xs text-slate-200 focus:border-sky-400"
            data-testid="filter-status"
          >
            <option value="all">Tous statuts</option>
            <option value="available">Disponibles</option>
            <option value="taken">Réservées</option>
          </select>
          {isAmiral && targets.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs border-rose-500/50 text-rose-400 hover:bg-rose-500/10"
              onClick={() => setShowClearAllDialog(true)}
              data-testid="btn-clear-all"
            >
              <Trash2 className="w-3 h-3 mr-1" />
              Effacer
            </Button>
          )}
        </div>

        {/* Cibles Disponibles - Tactical Navy Style */}
        <div className="bg-slate-900/70 border border-sky-950/80 rounded-lg p-3 mb-4">
          <h3 className="text-sm font-orbitron uppercase tracking-wider text-sky-400 flex items-center gap-2 mb-2">
            <Target className="w-4 h-4" />
            Cibles Disponibles ({availableTargets.length})
          </h3>
          {availableTargets.length === 0 ? (
            <p className="text-center py-4 text-slate-500 text-sm">Aucune cible disponible</p>
          ) : (
            <div className="space-y-1.5">
              {availableTargets.map(target => {
                const config = PRIORITY_CONFIG[target.priority] || PRIORITY_CONFIG.normal;
                const isLate = target.is_late;
                const isVA = target.va;
                
                // Styles différenciés: VA = doré premium, Late = gris informatif (pas urgent)
                let rowStyle = 'bg-slate-900/60 border-sky-950 hover:border-sky-500/40';
                if (isVA) {
                  // VA: fond doré premium avec effet brillant
                  rowStyle = 'bg-gradient-to-r from-amber-500/20 via-yellow-500/15 to-amber-500/20 border-amber-400/60 shadow-[0_0_10px_rgba(251,191,36,0.2)]';
                } else if (isLate) {
                  // Retard: fond gris subtil, juste informatif (>24h non traité)
                  rowStyle = 'bg-slate-700/30 border-slate-500/40';
                }
                
                return (
                  <div
                    key={target.id}
                    className={`p-2.5 rounded-lg border transition-all hover:bg-slate-800/50 ${rowStyle}`}
                    data-testid={`target-${target.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex items-center gap-1 flex-shrink-0 pt-0.5">
                        <span className="text-xs">{config.icon}</span>
                        {isLate && (
                          <span className="text-[9px] font-medium text-slate-400 bg-slate-600/50 px-1.5 py-0.5 rounded flex items-center gap-0.5">
                            +{target.report_count || 1}j
                          </span>
                        )}
                        {isVA && (
                          <span className="text-[9px] font-black text-amber-900 bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-300 px-1.5 py-0.5 rounded shadow-sm border border-amber-400/50">
                            ⭐ VA
                          </span>
                        )}
                        {target.source === 'external' && (
                          <Globe className="w-3 h-3 text-emerald-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        {target.url && target.url.startsWith('http') ? (
                          <a
                            href={target.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-xs underline decoration-sky-500/40 hover:decoration-sky-400 hover:text-sky-400 inline-flex items-start gap-1 font-medium cursor-pointer ${isVA ? 'text-amber-100' : 'text-sky-300'}`}
                          >
                            <ExternalLink className={`w-3 h-3 flex-shrink-0 mt-0.5 ${isVA ? 'text-amber-200' : 'text-sky-500'}`} />
                            <span>{target.title}</span>
                          </a>
                        ) : (
                          <span className={`text-xs font-medium ${isVA ? 'text-amber-100' : 'text-slate-200'}`}>{target.title}</span>
                        )}
                        {target.sites && target.sites.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {target.sites.map(siteId => (
                              <span key={siteId} className="text-[9px] font-medium text-sky-300 bg-sky-500/15 px-1.5 py-0.5 rounded">
                                {getSiteName(siteId)}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Créateur, date et source */}
                        <div className="flex items-center gap-1.5 mt-1 text-[9px] text-slate-500">
                          <Calendar className="w-2.5 h-2.5" />
                          <span>{formatDate(target.created_at)} {formatTime(target.created_at)}</span>
                          <span className="text-slate-600">·</span>
                          <span>par {target.created_by?.name || 'Inconnu'}</span>
                          {target.source === 'iOS Shortcut' && (
                            <span className="text-fuchsia-400 bg-fuchsia-500/20 px-1.5 py-0.5 rounded font-medium">
                              📱 iOS Shortcut
                            </span>
                          )}
                          {target.source === 'PWA Share Target' && (
                            <span className="text-violet-400 bg-violet-500/20 px-1.5 py-0.5 rounded font-medium">
                              📱 PWA
                            </span>
                          )}
                          {target.source === 'external' && (
                            <span className="text-emerald-400 bg-emerald-500/20 px-1.5 py-0.5 rounded font-medium">
                              🌐 Externe
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => openEditDialog(target)}
                          className="p-1 text-slate-500 hover:text-sky-400 transition-colors"
                          title="Modifier"
                          data-testid={`edit-btn-${target.id}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                        {isAmiral && (
                          <button
                            onClick={() => handleDelete(target.id)}
                            className="p-1 text-rose-500/50 hover:text-rose-400"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                        <Button
                          size="sm"
                          className={`h-6 text-[10px] px-2 font-bold ${
                            isVA 
                              ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-amber-950 border border-amber-300/50' 
                              : 'bg-sky-600 hover:bg-sky-500 text-white'
                          }`}
                          onClick={() => openTakeDialog(target)}
                          data-testid={`take-btn-${target.id}`}
                        >
                          PRENDRE
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Légende + Répartition - Entre disponibles et réservées */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4 px-3 py-2 bg-slate-900/30 border border-slate-800 rounded text-[10px] text-slate-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">🔴 Urgent</span>
            <span className="flex items-center gap-1">🟡 Normal</span>
            <span className="flex items-center gap-1">🟢 Basse</span>
            <span className="flex items-center gap-1">
              <span className="text-[8px] font-black text-amber-900 bg-gradient-to-r from-amber-300 via-yellow-300 to-amber-300 px-1 rounded">⭐ VA</span> Prioritaire
            </span>
            <span className="flex items-center gap-1">
              <span className="text-[8px] font-medium text-slate-400 bg-slate-600/50 px-1 rounded">+Xj</span> En attente
            </span>
            <span className="flex items-center gap-1"><Globe className="w-2.5 h-2.5 text-emerald-400" /> Externe</span>
          </div>
          {Object.keys(userDistribution).length > 0 && (
            <>
              <span className="text-slate-600">|</span>
              <div className="flex items-center gap-1 flex-wrap">
                <Users className="w-3 h-3 text-violet-400" />
                {Object.entries(userDistribution).sort((a, b) => b[1] - a[1]).map(([name, count], idx) => (
                  <span key={name} className="flex items-center gap-0.5">
                    {idx > 0 && <span className="text-slate-600 mx-1">·</span>}
                    <span className="text-violet-300">{name}</span>
                    <span className="font-bold text-violet-400 bg-violet-500/20 px-1 rounded">{count}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Cibles réservées - Tactical Navy Style */}
        <div className="bg-slate-900/70 border border-sky-950/80 rounded-lg p-3">
          <h3 className="text-sm font-orbitron uppercase tracking-wider text-blue-400 flex items-center gap-2 mb-2">
            <User className="w-4 h-4" />
            Réservées ({takenTargets.length})
          </h3>
          {takenTargets.length === 0 ? (
            <p className="text-center py-3 text-slate-500 text-sm">Aucune cible réservée</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {takenTargets.map(target => {
                const isMyTarget = target.taken_by?.user_id === user?.id;
                const canRelease = isMyTarget || isAmiral;
                return (
                  <div
                    key={target.id}
                    className={`p-2 rounded border border-blue-500/20 bg-blue-500/5 ${target.va ? 'ring-1 ring-amber-500/30' : ''}`}
                    data-testid={`taken-target-${target.id}`}
                  >
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        {target.va && (
                          <span className="text-[9px] font-bold text-amber-400 bg-amber-500/20 px-1 rounded mr-1">VA</span>
                        )}
                        {target.url && target.url.startsWith('http') ? (
                          <a href={target.url} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-300 underline decoration-sky-500/40 hover:decoration-sky-400 hover:text-sky-400 cursor-pointer break-words">
                            {target.title}
                          </a>
                        ) : (
                          <span className="text-xs text-slate-400 break-words">{target.title}</span>
                        )}
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-[10px] text-blue-300">{target.taken_by?.name}</span>
                          <span className="text-[9px] text-slate-500">• {formatTime(target.taken_at)}</span>
                        </div>
                        {target.sites && target.sites.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1.5 pt-1.5 border-t border-slate-700/50">
                            <span className="text-[9px] text-slate-500 mr-0.5">Sites:</span>
                            {target.sites.map(siteId => (
                              <span key={siteId} className="text-[9px] font-medium text-sky-300 bg-sky-500/15 px-1.5 py-0.5 rounded">
                                {getSiteName(siteId)}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Source seulement (pas de "Proposé par" pour les réservées) */}
                        {(target.source === 'iOS Shortcut' || target.source === 'PWA Share Target' || target.source === 'external') && (
                          <div className="flex items-center gap-1.5 mt-1 text-[9px] text-slate-600">
                            {target.source === 'iOS Shortcut' && (
                              <span className="text-fuchsia-400 bg-fuchsia-500/20 px-1.5 py-0.5 rounded font-medium">
                                📱 iOS
                              </span>
                            )}
                            {target.source === 'PWA Share Target' && (
                              <span className="text-violet-400 bg-violet-500/20 px-1.5 py-0.5 rounded font-medium">
                                📱 PWA
                              </span>
                            )}
                            {target.source === 'external' && (
                              <span className="text-emerald-400 bg-emerald-500/20 px-1.5 py-0.5 rounded font-medium">
                                🌐
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      {canRelease && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-5 text-[9px] px-1.5 border-slate-600 text-slate-400 hover:border-sky-500 hover:text-sky-400"
                          onClick={() => { setSelectedTarget(target); setShowReleaseDialog(true); }}
                          data-testid={`release-btn-${target.id}`}
                        >
                          LIBÉRER
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Section Archivées */}
        {showArchived && archivedTargets.length > 0 && (
          <div className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-3 mt-4" data-testid="archived-section">
            <h3 className="text-sm font-orbitron uppercase tracking-wider text-slate-400 flex items-center gap-2 mb-3">
              <Archive className="w-4 h-4" />
              Cibles Archivées ({archivedTargets.length})
            </h3>
            <div className="space-y-1.5">
              {archivedTargets.map(target => (
                <div
                  key={target.id}
                  className="flex items-start gap-3 p-2 rounded border border-slate-700/40 bg-slate-800/30"
                  data-testid={`archived-target-${target.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {target.va && (
                        <span className="text-[9px] font-bold text-amber-400/60 bg-amber-500/10 px-1 rounded">VA</span>
                      )}
                      {target.url && target.url.startsWith('http') ? (
                        <a href={target.url} target="_blank" rel="noopener noreferrer" 
                           className="text-xs text-slate-400 underline decoration-slate-600 hover:text-sky-400 hover:decoration-sky-400 cursor-pointer">
                          {target.title}
                        </a>
                      ) : (
                        <span className="text-xs text-slate-500">{target.title}</span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-[9px] text-slate-600">
                      {target.taken_by?.name && (
                        <span className="text-violet-400/70">
                          Prise par {target.taken_by.name}
                        </span>
                      )}
                      {target.archived_at && (
                        <span>Archivée le {new Date(target.archived_at).toLocaleDateString('fr-FR')}</span>
                      )}
                      {target.archived_reason && (
                        <span className="bg-slate-700/50 px-1.5 py-0.5 rounded text-slate-500">
                          {target.archived_reason === 'max_reports_reached' ? 'Expirée (non prise)' :
                           target.archived_reason === 'weekly_purge' ? 'Purge hebdo' : target.archived_reason}
                        </span>
                      )}
                      {!target.taken_by && !target.archived_reason && (
                        <span className="text-slate-600">Archivée automatiquement</span>
                      )}
                      <span className="text-slate-600">Proposée par {target.created_by?.name || 'Inconnu'}</span>
                    </div>
                  </div>
                  {isAmiral && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 text-[9px] px-2 border-emerald-600/40 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-500"
                      onClick={() => handleRestore(target.id)}
                      data-testid={`restore-btn-${target.id}`}
                    >
                      <RotateCcw className="w-2.5 h-2.5 mr-1" />
                      Restaurer
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dialog Ajouter - Tactical Navy Style */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="bg-slate-950/95 border-2 border-sky-500/40 shadow-[0_0_40px_rgba(56,189,248,0.2)] max-w-lg rounded-xl backdrop-blur-2xl text-slate-100">
            <DialogHeader>
              <DialogTitle className="text-sky-400 flex items-center gap-2 font-orbitron uppercase tracking-wider">
                <Plus className="w-5 h-5" />
                Nouvelle cible
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Proposez un sujet d'article à traiter par l'équipe
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-slate-300 font-semibold text-xs tracking-wider uppercase flex items-center gap-2">
                  Sujet ou URL *
                  {scraping && (
                    <span className="flex items-center gap-1 text-[10px] text-sky-400 normal-case font-normal">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Récupération du titre...
                    </span>
                  )}
                </Label>
                <div className="relative">
                  <Input
                    placeholder="Ex: https://... ou Crash boursier à Tokyo"
                    value={newTitle}
                    onChange={(e) => handleInputChange(e.target.value)}
                    className="bg-slate-900/80 border-slate-700 text-slate-100 placeholder:text-slate-500 focus:border-sky-400 focus:ring-1 focus:ring-sky-400 pr-8"
                    data-testid="input-title"
                  />
                  {newUrl && <Link className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-500" />}
                </div>
                {newUrl && (
                  <p className="text-[10px] text-sky-400 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" />
                    Lien attaché
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300 font-semibold text-xs tracking-wider uppercase">Priorité</Label>
                <RadioGroup value={newPriority} onValueChange={setNewPriority} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="urgent" id="urgent" />
                    <Label htmlFor="urgent" className="text-rose-400 cursor-pointer">🔴 Urgent</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="normal" id="normal" />
                    <Label htmlFor="normal" className="text-amber-400 cursor-pointer">🟡 Normal (défaut)</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="low" id="low" />
                    <Label htmlFor="low" className="text-emerald-400 cursor-pointer">🟢 Basse</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="flex items-center gap-3 py-2 px-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <input
                  type="checkbox"
                  id="va-checkbox"
                  checked={newVa}
                  onChange={(e) => setNewVa(e.target.checked)}
                  className="w-4 h-4 rounded border-amber-500 text-amber-500 focus:ring-amber-500 cursor-pointer"
                />
                <Label htmlFor="va-checkbox" className="text-amber-400 cursor-pointer flex items-center gap-2">
                  <span className="font-bold">VA</span>
                  <span className="text-[10px] text-amber-300/70">(mise en exergue)</span>
                </Label>
              </div>

              {/* Suggestions IA */}
              {(aiSuggestions.length > 0 || loadingSuggestions) && (
                <div className="space-y-2">
                  <Label className="text-slate-300 font-semibold text-xs tracking-wider uppercase flex items-center gap-2">
                    <Wand2 className="w-3.5 h-3.5 text-violet-400" />
                    Suggestions IA
                    {loadingSuggestions && <Loader2 className="w-3 h-3 animate-spin text-violet-400" />}
                  </Label>
                  {aiSuggestions.length > 0 && (
                    <div className="p-2.5 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-lg">
                      <div className="flex flex-wrap gap-1.5">
                        {aiSuggestions.map(sugg => {
                          const isSelected = selectedSites.includes(sugg.site_id);
                          return (
                            <button
                              key={sugg.site_id}
                              type="button"
                              onClick={() => applySuggestion(sugg.site_id)}
                              className={`group relative text-[10px] px-2 py-1.5 rounded transition-all font-semibold flex items-center gap-1 ${
                                isSelected 
                                  ? 'bg-violet-500 text-white ring-2 ring-violet-300' 
                                  : 'bg-slate-800 text-slate-200 hover:bg-violet-500/30 border border-violet-400/30'
                              }`}
                              title={sugg.reasons?.join(' • ') || 'Suggestion IA'}
                              data-testid={`ai-suggestion-${sugg.site_id}`}
                            >
                              {sugg.is_ai_suggestion && <Sparkles className="w-2.5 h-2.5 text-violet-300" />}
                              <span>{sugg.short_name || sugg.site_name}</span>
                              {sugg.learned && <span className="text-[8px] text-emerald-300">📚</span>}
                              {isSelected && <Check className="w-2.5 h-2.5" />}
                            </button>
                          );
                        })}
                      </div>
                      {aiSuggestions.some(s => s.reasons?.length > 0) && (
                        <p className="text-[9px] text-violet-300/70 mt-1.5 italic">
                          {aiSuggestions.find(s => s.reasons?.length > 0)?.reasons?.[0]}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {sites.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-300 font-semibold text-xs tracking-wider uppercase flex items-center gap-2">
                    Sites demandés
                    <span className="text-[10px] text-slate-500 normal-case font-normal">(optionnel)</span>
                  </Label>
                  <div className="flex flex-wrap gap-1.5 p-2 bg-slate-900 rounded border border-slate-700 max-h-32 overflow-y-auto">
                    {sites.map(site => {
                      const isSelected = selectedSites.includes(site.id);
                      // Vérifier si ce site est aussi une suggestion IA
                      const isSuggested = aiSuggestions.some(s => s.site_id === site.id);
                      let siteName = site.name || '';
                      if (site.url) {
                        try {
                          siteName = new URL(site.url).hostname.replace('www.', '').split('.')[0].toUpperCase();
                        } catch {}
                      }
                      return (
                        <button
                          key={site.id}
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSelectedSites(selectedSites.filter(id => id !== site.id));
                            } else {
                              setSelectedSites([...selectedSites, site.id]);
                            }
                          }}
                          className={`text-[10px] px-2 py-1 rounded transition-all font-semibold hover:opacity-80 flex items-center gap-1 ${
                            isSelected 
                              ? 'bg-sky-500 text-white' 
                              : isSuggested 
                                ? 'bg-violet-200 text-violet-900 ring-1 ring-violet-400' 
                                : 'bg-slate-200 text-slate-800'
                          }`}
                        >
                          {isSuggested && <AIBadge small />}
                          {siteName || site.id?.slice(0, 6)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Annuler
              </Button>
              <Button
                onClick={handleAddTarget}
                disabled={submitting || !newTitle.trim()}
                className="bg-sky-600 hover:bg-sky-500 text-white font-semibold"
                data-testid="btn-confirm-add"
              >
                {submitting ? 'Ajout...' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Libérer - Tactical Navy Style */}
        <Dialog open={showReleaseDialog} onOpenChange={setShowReleaseDialog}>
          <DialogContent className="bg-slate-950/95 border-2 border-sky-500/40 shadow-[0_0_40px_rgba(56,189,248,0.2)] rounded-xl backdrop-blur-2xl text-slate-100">
            <DialogHeader>
              <DialogTitle className="text-amber-400 flex items-center gap-2 font-orbitron uppercase tracking-wider">
                <AlertTriangle className="w-5 h-5" />
                Libérer cette cible ?
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                "{selectedTarget?.title}"
              </DialogDescription>
            </DialogHeader>
            <p className="text-sm text-slate-300 py-4">
              Elle redeviendra disponible pour tous les utilisateurs.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReleaseDialog(false)} className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Annuler
              </Button>
              <Button onClick={handleRelease} disabled={submitting} className="bg-sky-600 hover:bg-sky-500 text-white font-semibold">
                {submitting ? 'Libération...' : 'Confirmer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Priorité - Tactical Navy Style */}
        <Dialog open={showPriorityDialog} onOpenChange={setShowPriorityDialog}>
          <DialogContent className="bg-slate-950/95 border-2 border-sky-500/40 shadow-[0_0_40px_rgba(56,189,248,0.2)] rounded-xl backdrop-blur-2xl text-slate-100">
            <DialogHeader>
              <DialogTitle className="text-sky-400 font-orbitron uppercase tracking-wider">Modifier la priorité</DialogTitle>
              <DialogDescription className="text-slate-400">
                "{selectedTarget?.title}"
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <RadioGroup value={newPriority} onValueChange={setNewPriority} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="urgent" id="p-urgent" />
                  <Label htmlFor="p-urgent" className="text-rose-400 cursor-pointer">🔴 Urgent</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="normal" id="p-normal" />
                  <Label htmlFor="p-normal" className="text-amber-400 cursor-pointer">🟡 Normal</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="low" id="p-low" />
                  <Label htmlFor="p-low" className="text-emerald-400 cursor-pointer">🟢 Basse</Label>
                </div>
              </RadioGroup>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPriorityDialog(false)} className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Annuler
              </Button>
              <Button onClick={handleUpdatePriority} disabled={submitting} className="bg-sky-600 hover:bg-sky-500 text-white font-semibold">
                {submitting ? 'Mise à jour...' : 'Valider'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Tout effacer - Tactical Navy Style */}
        <Dialog open={showClearAllDialog} onOpenChange={setShowClearAllDialog}>
          <DialogContent className="bg-slate-950/95 border-2 border-rose-500/40 shadow-[0_0_40px_rgba(244,63,94,0.2)] rounded-xl backdrop-blur-2xl text-slate-100">
            <DialogHeader>
              <DialogTitle className="text-rose-400 flex items-center gap-2 font-orbitron uppercase tracking-wider">
                <Trash2 className="w-5 h-5" />
                Vider toutes les cibles ?
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <p className="text-sm text-slate-300">
                Vous êtes sur le point de supprimer <span className="font-bold text-rose-400">{targets.length} cible{targets.length > 1 ? 's' : ''}</span>.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowClearAllDialog(false)} className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Annuler
              </Button>
              <Button onClick={handleClearAll} disabled={submitting} className="bg-rose-600 hover:bg-rose-500 text-white font-semibold">
                {submitting ? 'Suppression...' : 'Tout supprimer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Prendre une cible avec Suggestions IA */}
        <Dialog open={showTakeDialog} onOpenChange={(open) => {
          setShowTakeDialog(open);
          if (!open) {
            setSelectedTarget(null);
            setTakeSites([]);
            setAiSuggestions([]);
          }
        }}>
          <DialogContent className="bg-slate-950/95 border-2 border-violet-500/40 shadow-[0_0_40px_rgba(139,92,246,0.2)] rounded-xl backdrop-blur-2xl text-slate-100 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-violet-400 flex items-center gap-2 font-orbitron uppercase tracking-wider text-sm">
                <Wand2 className="w-4 h-4" />
                Prendre cette cible
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-xs">
                Sélectionnez les débouchés pour cet article. Vos choix améliorent les suggestions futures.
              </DialogDescription>
            </DialogHeader>
            
            {selectedTarget && (
              <div className="py-3 space-y-4">
                {/* Titre de la cible */}
                <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                  <p className="text-sm text-slate-200 font-medium leading-tight">{selectedTarget.title}</p>
                  {selectedTarget.url && (
                    <a href={selectedTarget.url} target="_blank" rel="noopener noreferrer" 
                       className="text-[10px] text-sky-400 hover:underline mt-1 inline-flex items-center gap-1">
                      <ExternalLink className="w-2.5 h-2.5" />
                      Source
                    </a>
                  )}
                </div>

                {/* Suggestions IA */}
                <div className="space-y-2">
                  <Label className="text-slate-300 font-semibold text-xs tracking-wider uppercase flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-violet-400" />
                    Suggestions IA
                    {loadingSuggestions && <Loader2 className="w-3 h-3 animate-spin text-violet-400" />}
                  </Label>
                  {aiSuggestions.length > 0 ? (
                    <div className="p-2.5 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/30 rounded-lg">
                      <div className="flex flex-wrap gap-1.5">
                        {aiSuggestions.map(sugg => {
                          const isSelected = takeSites.includes(sugg.site_id);
                          return (
                            <button
                              key={sugg.site_id}
                              type="button"
                              onClick={() => {
                                if (isSelected) {
                                  setTakeSites(takeSites.filter(id => id !== sugg.site_id));
                                } else {
                                  setTakeSites([...takeSites, sugg.site_id]);
                                }
                              }}
                              className={`group text-[10px] px-2 py-1.5 rounded transition-all font-semibold flex items-center gap-1 ${
                                isSelected 
                                  ? 'bg-violet-500 text-white ring-2 ring-violet-300' 
                                  : 'bg-slate-800 text-slate-200 hover:bg-violet-500/30 border border-violet-400/30'
                              }`}
                              title={sugg.reasons?.join(' • ') || 'Suggestion IA'}
                              data-testid={`take-suggestion-${sugg.site_id}`}
                            >
                              {sugg.is_ai_suggestion && <Sparkles className="w-2.5 h-2.5 text-violet-300" />}
                              <span>{sugg.short_name || sugg.site_name}</span>
                              {sugg.learned && <span className="text-[8px] text-emerald-300">📚</span>}
                              {isSelected && <Check className="w-2.5 h-2.5" />}
                            </button>
                          );
                        })}
                      </div>
                      {aiSuggestions.some(s => s.reasons?.length > 0) && (
                        <p className="text-[9px] text-violet-300/70 mt-1.5 italic">
                          {aiSuggestions.find(s => s.reasons?.length > 0)?.reasons?.[0]}
                        </p>
                      )}
                    </div>
                  ) : !loadingSuggestions && (
                    <p className="text-[10px] text-slate-500 italic">Pas de suggestions pour ce sujet</p>
                  )}
                </div>

                {/* Tous les sites */}
                {sites.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-slate-300 font-semibold text-xs tracking-wider uppercase">
                      Tous les sites
                    </Label>
                    <div className="flex flex-wrap gap-1.5 p-2 bg-slate-900 rounded border border-slate-700 max-h-28 overflow-y-auto">
                      {sites.map(site => {
                        const isSelected = takeSites.includes(site.id);
                        const isSuggested = aiSuggestions.some(s => s.site_id === site.id);
                        let siteName = site.name || '';
                        if (site.url) {
                          try {
                            siteName = new URL(site.url).hostname.replace('www.', '').split('.')[0].toUpperCase();
                          } catch {}
                        }
                        return (
                          <button
                            key={site.id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setTakeSites(takeSites.filter(id => id !== site.id));
                              } else {
                                setTakeSites([...takeSites, site.id]);
                              }
                            }}
                            className={`text-[10px] px-2 py-1 rounded transition-all font-semibold hover:opacity-80 flex items-center gap-1 ${
                              isSelected 
                                ? 'bg-sky-500 text-white' 
                                : isSuggested 
                                  ? 'bg-violet-200 text-violet-900 ring-1 ring-violet-400' 
                                  : 'bg-slate-200 text-slate-800'
                            }`}
                          >
                            {isSuggested && <AIBadge small />}
                            {siteName || site.id?.slice(0, 6)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Résumé */}
                {takeSites.length > 0 && (
                  <div className="text-[10px] text-emerald-400 flex items-center gap-1">
                    <Check className="w-3 h-3" />
                    {takeSites.length} site{takeSites.length > 1 ? 's' : ''} sélectionné{takeSites.length > 1 ? 's' : ''} — votre choix améliore l'IA
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setShowTakeDialog(false)} 
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleTake} 
                disabled={submitting} 
                className="bg-violet-600 hover:bg-violet-500 text-white font-semibold"
                data-testid="confirm-take-btn"
              >
                {submitting ? 'Réservation...' : 'Prendre la cible'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog Modifier une cible */}
        <Dialog open={showEditDialog} onOpenChange={(open) => { if (!open) { setShowEditDialog(false); setEditTarget(null); } }}>
          <DialogContent className="bg-slate-950/95 border-2 border-sky-500/40 shadow-[0_0_40px_rgba(56,189,248,0.2)] max-w-lg rounded-xl backdrop-blur-2xl text-slate-100">
            <DialogHeader>
              <DialogTitle className="text-sky-400 flex items-center gap-2 font-orbitron uppercase tracking-wider text-sm">
                <Pencil className="w-4 h-4" />
                Modifier la cible
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-xs">
                Modifiez les informations de cette cible
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-3">
              {/* Titre */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold">Titre *</Label>
                <Input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="bg-slate-900/80 border-slate-700 text-slate-100 focus:border-sky-400 text-sm"
                  data-testid="edit-title-input"
                />
              </div>

              {/* URL */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold">URL source</Label>
                <Input
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  placeholder="https://..."
                  className="bg-slate-900/80 border-slate-700 text-slate-100 focus:border-sky-400 text-sm"
                  data-testid="edit-url-input"
                />
              </div>

              {/* Priorité */}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold">Priorité</Label>
                <RadioGroup value={editPriority} onValueChange={setEditPriority} className="flex gap-3">
                  <div className="flex items-center gap-1.5">
                    <RadioGroupItem value="urgent" id="edit-urgent" className="border-red-500 text-red-500" />
                    <Label htmlFor="edit-urgent" className="text-red-400 cursor-pointer text-xs">🔴 Urgent</Label>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <RadioGroupItem value="normal" id="edit-normal" className="border-yellow-500 text-yellow-500" />
                    <Label htmlFor="edit-normal" className="text-amber-400 cursor-pointer text-xs">🟡 Normal</Label>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <RadioGroupItem value="low" id="edit-low" className="border-green-500 text-green-500" />
                    <Label htmlFor="edit-low" className="text-green-400 cursor-pointer text-xs">🟢 Basse</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* VA */}
              <div className="flex items-center gap-3 p-2 rounded bg-slate-900/50 border border-slate-700">
                <button
                  type="button"
                  onClick={() => setEditVa(!editVa)}
                  className={`w-10 h-5 rounded-full transition-colors relative ${editVa ? 'bg-amber-500' : 'bg-slate-700'}`}
                  data-testid="edit-va-toggle"
                >
                  <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${editVa ? 'left-5' : 'left-0.5'}`} />
                </button>
                <Label className={`text-xs font-semibold cursor-pointer ${editVa ? 'text-amber-400' : 'text-slate-400'}`} onClick={() => setEditVa(!editVa)}>
                  ⭐ Valeur Ajoutée (VA)
                </Label>
              </div>

              {/* Sites */}
              {sites.length > 0 && (
                <div className="space-y-1.5">
                  <Label className="text-slate-300 text-xs uppercase tracking-wider font-semibold">Sites cibles</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {sites.map(site => {
                      const isSelected = editSites.includes(site.id);
                      let siteName;
                      try { siteName = new URL(site.url).hostname.replace('www.', '').split('.')[0].toUpperCase(); }
                      catch { siteName = site.name?.slice(0, 10) || site.id.slice(0, 6); }
                      return (
                        <button
                          key={site.id}
                          type="button"
                          onClick={() => setEditSites(prev => isSelected ? prev.filter(id => id !== site.id) : [...prev, site.id])}
                          className={`text-[10px] px-2 py-1 rounded-md border transition-all font-medium ${
                            isSelected
                              ? 'bg-sky-500/20 border-sky-400/60 text-sky-300'
                              : 'bg-slate-800/50 border-slate-700 text-slate-500 hover:border-slate-500 hover:text-slate-300'
                          }`}
                          data-testid={`edit-site-${site.id}`}
                        >
                          {siteName}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowEditDialog(false); setEditTarget(null); }} className="border-slate-600 text-slate-300 hover:bg-slate-800">
                Annuler
              </Button>
              <Button
                onClick={handleEdit}
                disabled={submitting || !editTitle.trim()}
                className="bg-sky-600 hover:bg-sky-500 text-white"
                data-testid="edit-save-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Popup d'alerte horaire de production */}
      <HourlyAlertPopup enabled={true} />
    </div>
  );
};

export default CiblesPage;
